import { Inject } from '@odinjs/odin';
import { FetchAPI, FieldAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBlur, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';

import { AdjustInfusionSpeedAliases, FragmentNames } from '../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import timeService from '../../../services/time-service';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../utils/parameters';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { MedicineAndSolutionsParams } from '../eletronic-prescription/medicine-and-solutions/medicine-and-solutions-params';
import { InfusionsViewerParams } from '../infusion-viewer/infusions-viewer-params';
import { PrescriptionType } from '../services/timeline-service';
import { ErrorDescriptionProps, FormValuesProps, SolutionType, UnitMeasure } from './adjust-infusion-speed-types';
import { ChangeType } from '../../../constants/infusion-solutions';

@Controller({ component: AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_LAYOUT, fragment: FragmentNames.ADJUST_INFUSION_SPEED })
export default class AdjustInfusionSpeedController extends BaseController {

  @Inject(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_FORM)
  private adjustInfusionSpeedFormApi!: FormAPI;

  @Inject(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_REASON_FIELD)
  private reasonFieldApi!: FieldAPI;

  @OnMount(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({
      encounterId,
      patientId,
    });
  }

  @OnMount(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_FORM)
  handleMountForm(): void {
    timeService.getCurrentDateTime()
      .then(currentDateTime => this.context.set({ currentDateTime }))
      .catch(() => this.context.set({ currentDateTime: moment().format(ISO_FULL_DATE_FORMAT) }))
      .finally(() => this.setFormData())
  }

  @OnSubmit(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_FORM)
  handleSubmitForm(event: Record<string, Value>): void {
    this.handleAdjustInfusionSpeed(event);
  }

  @OnBeforeCancel(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_FORM)
  handleCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }

  @OnBlur(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_INFUSION_SPEED_INPUT)
  handleBlur({ target }: ChangeEvent<string>): void {
    const value = target.getValue() as unknown as string;
    const formattedValue = value.replace(/,/g, '.');
    target.setValue(formattedValue);
  }

  @OnMount(AdjustInfusionSpeedAliases.ADJUST_INFUSION_SPEED_REASON_FETCH)
  handleFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: PrescriptionType.ADULT_PARENTERAL_NUTRITION }, { skip: true });
  }

  private checkShowReasonByPrescriptionType(prescriptionType: PrescriptionType): boolean {
    const allowedTypes = [
      PrescriptionType.NEONATAL_PARENTERAL_NUTRITION,
      PrescriptionType.NEONATAL_PARENTERAL_PEDIATRIC,
      PrescriptionType.ADULT_PARENTERAL_NUTRITION
    ];

    return allowedTypes.includes(prescriptionType);
  }

  private setFormData(): void {
    const dosageTypeDescription = InfusionsViewerParams.adminSchedule.dosageTypeDescription;
    const { itemType, isTypeDosage } = InfusionsViewerParams.infusionDetailsParams;
    const itemTypeValid = itemType !== undefined && itemType !== '';
    const infusionSpeed = String(InfusionsViewerParams.params.currentSpeedInfusion) ?? 0;
    const dateTime = this.context.get('currentDateTime');
    const currentRecord = this.adjustInfusionSpeedFormApi.getRecord();
    const unitMeasure = {
      dosageType: isTypeDosage,
      unitMeasure: dosageTypeDescription,
    } as UnitMeasure

    this.adjustInfusionSpeedFormApi.setData({
      ...currentRecord,
      dateTime,
      infusionSpeed,
      unitMeasure,
    })

    this.reasonFieldApi.setVisible(itemTypeValid && this.checkShowReasonByPrescriptionType(itemType as PrescriptionType))
  }

  private handleAdjustInfusionSpeed(event: Record<string, Value>): void {
    const tasyParamFiftyNine = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.CONSIST_SOLUTIONS_IF_EXIST_SOLUTIONS_OF_PRESCRIPTIONS_PREVIOUS_PENDING);
    const tasyParamSeventyNine = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.COMPARE_START_SOLUTIONS_IF_EXIST_SOLUTIONS_OF_PRESCRIPTIONS_PREVIOUS_PENDING);
    const tasyParamOneHundredEightySeven = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.FIRST_FILTER_TIME);
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { unitMeasure, dateTime, infusionSpeed, justification, reason } = event.record as FormValuesProps;
    const userName = foundationContext.getUser()?.username;
    const abbreviationUnitMeasure = unitMeasure.unitMeasureValue
    const formatDateTime = moment(String(dateTime)).format(ISO_FULL_DATE_FORMAT);
    const reasonSequenceNumber = reason?.sequenceNumber || null
    MedicineAndSolutionsParams.speedParam = { currentSpeedInfusion: Number(infusionSpeed) };

    const body = {
      changeDate: formatDateTime,
      changeType: ChangeType.ADJUST_SPEED,
      dosageType: abbreviationUnitMeasure,
      encounterNumber: Number(encounterId),
      establishmentCode: Number(InfusionsViewerParams.params.establishmentCode),
      factorConsistType: tasyParamSeventyNine,
      infusionVelocityQuantity: Number(infusionSpeed),
      justificationDescription: String(justification ?? ''),
      prescriptionNumber: Number(InfusionsViewerParams.params.prescriptionNumber),
      reasonSequenceNumber,
      requireAppointmentType: tasyParamOneHundredEightySeven,
      solutionSequenceNumber: Number(InfusionsViewerParams.params.solutionNumber),
      solutionType: this.getSolutionType(),
      startConsistType: tasyParamFiftyNine,
      userName,
    } as Record<string, Value>;

    this.fetchGenerateChangePrescription(body)
  }

  private fetchGenerateChangePrescription(generateChangePrescriptionRequest: Record<string, Value>): void {
    HttpService.post<ErrorDescriptionProps>(Endpoint.ADEP_GENERATE_CHANGE_SOL_PRESCR, {}, { body: generateChangePrescriptionRequest })
      .then(({ body }) => {
        const messageError = body?.returnDescription;

        if (messageError) {
          this.showToast(messageError, ToastLevel.WARNING);
          return
        }

        this.showToast(MessageService.translate('text.adjust-infusion-speed-with-success'), ToastLevel.SUCCESS);
        this.redirectToTimeline();
      })
      .catch(error => {
        const messageError = error.body.errors[0];
        this.showToast(messageError, ToastLevel.ERROR);
      })
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
      encounterId,
      patientId,
    });
    applicationContext.clearNavigationStack();
  }

  private showToast(message: string, typeMessage: ToastLevel): void {
    let dismissible = true;

    if (message === MessageService.translate('text.adjust-infusion-speed-with-success')) {
      dismissible = false;
    }

    ToastService.open({
      dismissExpression: message,
      dismissible,
      highlight: false,
      id: 'adjust-infusion-speed',
      level: typeMessage,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  private getSolutionType(): number {
    const itemType = InfusionsViewerParams.infusionDetailsParams?.itemType
    switch (itemType) {
      case PrescriptionType.ENTERAL_NUTRITION:
        return SolutionType.ENTERAL_NUTRITION;
      case PrescriptionType.ADULT_PARENTERAL_NUTRITION:
        return SolutionType.ADULT_PARENTERAL_NUTRITION;
      case PrescriptionType.NEONATAL_PARENTERAL_NUTRITION:
        return SolutionType.NEONATAL_PARENTERAL_NUTRITION;
      case PrescriptionType.NEONATAL_PARENTERAL_PEDIATRIC:
        return SolutionType.NEONATAL_PARENTERAL_PEDIATRIC;
      default:
        return SolutionType.SOLUTION;
    }
  }
}

